// T010: Implement useQuickPost hook
// T014: Implement form reset and feed refresh on success
// T018: Integrate generateTitleFromBody (US3)
// T024: Add image upload logic (US2)
// T027: Add image URL to post body (US2)

import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createPost } from '@/services/steemit/postService'
import { uploadImage } from '@/services/imageUpload'
import { useAuthStore } from '@/stores/authStore'
import { extractHashtags, buildTagsArray } from '@/utils/hashtagExtractor'
import { generateQuickPostPermlink } from '@/utils/permlink'
import { generateTitleFromBody } from '@/utils/titleGenerator'
import { queryKeys } from '@/services/queryClient'
import type {
  QuickPostInput,
  QuickPostStatus,
  QuickPostResult,
  UseQuickPostReturn,
  QuickPostData,
} from '@/types/quickPost'
import { QUICK_POST_CONSTANTS } from '@/types/quickPost'

/**
 * Prepares QuickPostData from user input
 * T027: Adds image URL to body end in markdown format
 */
function prepareQuickPostData(input: QuickPostInput, imageUrl?: string): QuickPostData {
  let { body } = input

  // T027: Add image URL to post body (at end, markdown format)
  if (imageUrl) {
    body = `${body}\n\n![](${imageUrl})`
  }

  // T018: Generate title from body (US3)
  const title = generateTitleFromBody(input.body, QUICK_POST_CONSTANTS.TITLE_MAX_BYTES)

  // Extract hashtags and build tags array
  const extractedTags = extractHashtags(input.body)
  const tags = buildTagsArray(
    extractedTags,
    QUICK_POST_CONSTANTS.DEFAULT_TAG,
    QUICK_POST_CONSTANTS.MAX_TAGS
  )

  // Generate unique permlink
  const permlink = generateQuickPostPermlink()

  return {
    title,
    body,
    tags,
    permlink,
    imageUrl,
  }
}

export function useQuickPost(): UseQuickPostReturn {
  const [status, setStatus] = useState<QuickPostStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)

  const user = useAuthStore((state) => state.user)
  const refreshRC = useAuthStore((state) => state.refreshRC)
  const queryClient = useQueryClient()

  const submit = useCallback(
    async (input: QuickPostInput): Promise<QuickPostResult> => {
      // Check authentication
      if (!user) {
        const errorMsg = 'You must be logged in to post'
        setError(errorMsg)
        setStatus('error')
        return { success: false, error: errorMsg }
      }

      // Validate input
      if (!input.body.trim()) {
        const errorMsg = 'Post content cannot be empty'
        setError(errorMsg)
        setStatus('error')
        return { success: false, error: errorMsg }
      }

      try {
        setError(null)
        setUploadProgress(0)

        let imageUrl: string | undefined

        // T024: Upload image if provided
        if (input.image) {
          setStatus('uploading')

          const uploadResult = await uploadImage({
            file: input.image,
            username: user.username,
            postingKey: user.postingKey,
            onProgress: (progress) => {
              setUploadProgress(progress.percentage)
            },
          })

          if (!uploadResult.success) {
            const errorMsg = uploadResult.error || 'Image upload failed'
            setError(errorMsg)
            setStatus('error')
            return {
              success: false,
              error: errorMsg,
              imageUploadFailed: true,
            }
          }

          imageUrl = uploadResult.url
        }

        // Set posting status
        setStatus('posting')

        // Prepare post data (T027: image URL added to body here)
        const postData = prepareQuickPostData(input, imageUrl)

        // Create post on blockchain
        const result = await createPost({
          author: user.username,
          postingKey: user.postingKey,
          title: postData.title,
          body: postData.body,
          tags: postData.tags,
          images: imageUrl ? [imageUrl] : undefined,
        })

        if (!result.success) {
          setError(result.error || 'Failed to create post')
          setStatus('error')
          return { success: false, error: result.error }
        }

        // Success!
        setStatus('success')

        // T014: Refresh feed to show new post
        await queryClient.invalidateQueries({
          queryKey: queryKeys.feed(user.username),
        })

        // Refresh RC after posting
        await refreshRC()

        return { success: true, permlink: result.permlink }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred'
        setError(errorMsg)
        setStatus('error')
        return { success: false, error: errorMsg }
      }
    },
    [user, queryClient, refreshRC]
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setError(null)
    setUploadProgress(0)
  }, [])

  return {
    status,
    error,
    uploadProgress,
    submit,
    reset,
  }
}
